import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  Receipt,
  Pencil,
  Trash2,
  Eye,
  MoreVertical,
  Plus,
  Download,
  Printer,
  Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PurchaseReceiptDialog from './PurchaseReceiptDialog';
import purchaseService from '../services/purchaseService';
import authService from '../services/authService';

const PurchaseReceiptList = ({
  purchaseOrder,
  onReceiptChange = null,
  readOnly = false
}) => {
  const { toast } = useToast();
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'admin';
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog states
  const [receiptDialog, setReceiptDialog] = useState({
    open: false,
    mode: 'create', // create, edit, view
    receiptData: null
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    receipt: null
  });

  const loadReceipts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Get receipts for this purchase order
      const response = await purchaseService.getReceipts(purchaseOrder._id);
      const receipts = Array.isArray(response) ? response : response.receipts || [];

      // Debug: Log receipt data structure
      console.log('Raw receipts data:', receipts);

      // Debug: Check if latest receipt has inspector_name
      if (receipts.length > 0) {
        const latestReceipt = receipts[receipts.length - 1];
        console.log('Latest receipt (raw):', latestReceipt);
        console.log('Latest receipt inspector_name:', latestReceipt.inspector_name);
        console.log('Latest receipt keys:', Object.keys(latestReceipt));
      }

      // Process receipts to ensure required fields exist
      const processedReceipts = receipts.map(receipt => {
        console.log('Processing receipt:', receipt);
        console.log('Receipt inspector_name before processing:', receipt.inspector_name);

        // Calculate receipt_total_quantity if missing (for backward compatibility)
        if ((!receipt.receipt_total_quantity || receipt.receipt_total_quantity === 0) && receipt.received_items) {
          receipt.receipt_total_quantity = receipt.received_items.reduce(
            (sum, item) => sum + (item.quantity_received || 0), 0
          );
          console.log('Calculated receipt_total_quantity:', receipt.receipt_total_quantity);
        }

        // Handle different possible field names for backward compatibility
        if (!receipt.receipt_total_quantity && receipt.total_quantity) {
          receipt.receipt_total_quantity = receipt.total_quantity;
        }

        // Ensure inspector_name exists
        if (!receipt.inspector_name) {
          receipt.inspector_name = '未知';
          console.log('Inspector name was missing, set to default:', receipt.inspector_name);
        } else {
          console.log('Inspector name found:', receipt.inspector_name);
        }

        console.log('Processed receipt with inspector:', receipt.inspector_name);
        return receipt;
      });

      setReceipts(processedReceipts);

    } catch (err) {
      console.error('Error loading receipts:', err);
      setError('Failed to load receipt history');
    } finally {
      setLoading(false);
    }
  }, [purchaseOrder]);

  useEffect(() => {
    if (purchaseOrder?._id) {
      loadReceipts();
    }
  }, [purchaseOrder, loadReceipts]);

  const handleCreateReceipt = () => {
    setReceiptDialog({
      open: true,
      mode: 'create',
      receiptData: null
    });
  };

  const handleEditReceipt = (receipt) => {
    setReceiptDialog({
      open: true,
      mode: 'edit',
      receiptData: receipt
    });
  };

  const handleViewReceipt = (receipt) => {
    setReceiptDialog({
      open: true,
      mode: 'view',
      receiptData: receipt
    });
  };

  const handleDeleteReceipt = (receipt) => {
    setDeleteDialog({
      open: true,
      receipt: receipt
    });
  };

  const handleReceiptSubmit = async (receiptData) => {
    try {
      let result;

      if (receiptDialog.mode === 'create') {
        result = await purchaseService.addReceipt(purchaseOrder._id, receiptData);
      } else if (receiptDialog.mode === 'edit') {
        result = await purchaseService.updateReceipt(receiptDialog.receiptData._id, receiptData);
      }

      // Reload receipts
      await loadReceipts();

      // Notify parent component
      if (onReceiptChange) {
        onReceiptChange(result);
      }

      setReceiptDialog({ open: false, mode: 'create', receiptData: null });
      toast({
        title: "成功",
        description: receiptDialog.mode === 'create' ? '收货记录已创建' : '收货记录已更新',
      });

    } catch (error) {
      console.error('Error saving receipt:', error);
      throw error; // Let the dialog handle the error display
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const receiptId = deleteDialog.receipt.receipt_id || deleteDialog.receipt._id;
      await purchaseService.deleteReceipt(purchaseOrder._id, receiptId);
      await loadReceipts();

      if (onReceiptChange) {
        onReceiptChange({ deleted: true });
      }

      setDeleteDialog({ open: false, receipt: null });
      toast({
        title: "成功",
        description: '收货记录已删除',
      });

    } catch (error) {
      console.error('Error deleting receipt:', error);
      setError('Failed to delete receipt');
      toast({
        title: "错误",
        description: '删除失败: ' + (error.response?.data?.error || error.message),
        variant: "destructive",
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const calculateTotals = () => {
    const totalReceived = isAdmin ? receipts.reduce((sum, receipt) => sum + (receipt.receipt_total_cost || 0), 0) : 0;
    const totalQuantity = receipts.reduce((sum, receipt) => sum + (receipt.receipt_total_quantity || 0), 0);

    return { totalReceived, totalQuantity };
  };

  const { totalReceived, totalQuantity } = calculateTotals();

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">收货记录</h3>
          {!readOnly && (
            <Button
              size="sm"
              onClick={handleCreateReceipt}
              disabled={purchaseOrder?.approval_status !== 'approved'}
            >
              <Plus className="h-4 w-4 mr-2" />
              新建收货记录
            </Button>
          )}
        </div>

        <Separator className="mb-4" />

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Summary */}
        {receipts.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium mb-2">收货汇总</h4>
            <div className="flex gap-6">
              {isAdmin && (
                <p className="text-sm text-muted-foreground">
                  累计收货金额: <strong>{formatCurrency(totalReceived)}</strong>
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                累计收货数量: <strong>{totalQuantity}</strong> 件
              </p>
              <p className="text-sm text-muted-foreground">
                收货记录数: <strong>{receipts.length}</strong> 条
              </p>
            </div>
          </div>
        )}

        {/* Receipts Table */}
        {receipts.length > 0 ? (
          <div className="border rounded-lg overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">收货日期</th>
                  <th className="text-left p-3 font-medium">验收人员</th>
                  {!readOnly && <th className="text-center p-3 font-medium">操作</th>}
                </tr>
              </thead>
              <tbody>
                {receipts.map((receipt, index) => (
                  <tr key={receipt._id || index} className="border-b hover:bg-muted">
                    <td className="p-3">
                      {formatDate(receipt.receipt_date)}
                    </td>
                    <td className="p-3">
                      {receipt.inspector_name || '-'}
                    </td>
                    {!readOnly && (
                      <td className="p-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewReceipt(receipt)}>
                              <Eye className="h-4 w-4 mr-2" />
                              查看详情
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditReceipt(receipt)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              编辑记录
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteReceipt(receipt)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              删除记录
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Printer className="h-4 w-4 mr-2" />
                              打印收货单
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="h-4 w-4 mr-2" />
                              导出数据
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-muted-foreground mb-2">
              暂无收货记录
            </h4>
            <p className="text-sm text-muted-foreground mb-4">
              此采购单尚未创建任何收货记录
            </p>
            {!readOnly && (
              <Button
                onClick={handleCreateReceipt}
                disabled={purchaseOrder?.approval_status !== 'approved'}
              >
                <Plus className="h-4 w-4 mr-2" />
                创建第一条收货记录
              </Button>
            )}
          </div>
        )}

        {/* Receipt Dialog */}
        <PurchaseReceiptDialog
          open={receiptDialog.open}
          mode={receiptDialog.mode}
          onClose={() => setReceiptDialog({ open: false, mode: 'create', receiptData: null })}
          onSubmit={handleReceiptSubmit}
          purchaseOrder={purchaseOrder}
          initialData={receiptDialog.receiptData}
          /* Optional: pass client context if available in parent */
          selectedClientId={purchaseOrder?.client_id || null}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onOpenChange={() => setDeleteDialog({ open: false, receipt: null })}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>确认删除收货记录</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p>
                确定要删除收货记录 "{deleteDialog.receipt?.receipt_id}" 吗？
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                此操作不可撤销，请谨慎操作。
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialog({ open: false, receipt: null })}
              >
                取消
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
              >
                确认删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PurchaseReceiptList;
